import { createRoute } from "@tanstack/react-router";
import { checkAdminRedirect, requireAuth } from "@/lib/authGuards";
import LandingContainer from "@/features/landing/components/LandingContainer";
import ServiceStatusContainer from "@/features/status/pages/ServiceStatusContainer";
import PrivacyPolicyContainer from "@/features/legal/pages/PrivacyPolicy";
import TermsConditionsContainer from "@/features/legal/pages/TermsConditionsContainer";
import LoginContainer from "@/features/auth/pages/LoginContainer";
import RegisterContainer from "@/features/auth/pages/RegisterContainer";
import HashContainer from "@/features/auth/pages/HashContainer";
import VerifyEmailContainer from "@/features/auth/pages/VerifyEmailContainer";
import ForgotPasswordContainer from "@/features/auth/pages/ForgotPasswordContainer";
import AuthCallback from "@/features/auth/components/Auth/AuthCallback";
import ChangelogContainer from "@/features/changelogs/pages/ChangelogContainer";
import SettingsContainer from "@/features/auth/pages/SettingsContainer";
import type { AnyRoute } from "@tanstack/react-router";

export function createPublicRoutes(rootRoute: AnyRoute) {
  const landingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/",
    component: LandingContainer,
    beforeLoad: checkAdminRedirect,
    staticData: { layout: "fullpage" },
    validateSearch: (search: Record<string, unknown>) => ({
      section: typeof search.section === "string" ? search.section : undefined,
    }),
  });

  const serviceStatusRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/service-status",
    component: ServiceStatusContainer,
    beforeLoad: requireAuth,
  });

  const privacyPolicyRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/privacy-policy",
    component: PrivacyPolicyContainer,
    staticData: { layout: "fullpage" },
  });

  const termsConditionsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/terms-conditions",
    component: TermsConditionsContainer,
    staticData: { layout: "fullpage" },
  });

  const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/login",
    component: LoginContainer,
    staticData: { layout: "auth" },
  });

  const registerRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/register",
    component: RegisterContainer,
    staticData: { layout: "auth" },
  });

  const hashRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/login/hash",
    component: HashContainer,
    staticData: { layout: "auth" },
  });

  const verifyEmailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/verify-email",
    component: VerifyEmailContainer,
    staticData: { layout: "auth" },
  });

  const forgotPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/forgot-password",
    component: ForgotPasswordContainer,
    staticData: { layout: "auth" },
    validateSearch: (search: Record<string, unknown>) => ({
      email: search.email ? String(search.email) : undefined,
      token: search.token ? String(search.token) : undefined,
      user_id: search.user_id ? String(search.user_id) : undefined,
    }),
  });

  const authCallbackRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/auth/callback",
    component: AuthCallback,
    staticData: { layout: "auth" },
  });

  const settingsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/settings",
    component: SettingsContainer,
    beforeLoad: requireAuth,
  });

  const changelogsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/changelogs",
    component: ChangelogContainer,
    beforeLoad: requireAuth,
  });

  return [
    landingRoute,
    serviceStatusRoute,
    privacyPolicyRoute,
    termsConditionsRoute,
    loginRoute,
    registerRoute,
    hashRoute,
    verifyEmailRoute,
    forgotPasswordRoute,
    authCallbackRoute,
    settingsRoute,
    changelogsRoute,
  ] as const;
}
